import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ModuleRepositoryExample, RepositoryPayload } from '@ca/core/models';
import { RepositoryService, UserNotificationService } from '@ca/core/services';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { MODULE_TITLE_PATTERN, USER_MODULES_ROUTE } from '../../constants/constants';

@Component({
    selector: 'ca-module-creator',
    templateUrl: './module-creator.component.html',
    styleUrls: ['./module-creator.component.scss'],
})
export class ModuleCreatorComponent implements OnInit {
    @Input() moduleExamples: ModuleRepositoryExample[] = [];

    placeHolderValue = '';
    formGroup: UntypedFormGroup;
    repository: RepositoryPayload = new RepositoryPayload();
    isSubmitting: boolean = false;

    constructor(
        private formBuilder: UntypedFormBuilder,
        private repositoryService: RepositoryService,
        private userNotificationService: UserNotificationService,
        private activeModal: NgbActiveModal,
        private translateService: TranslateService,
        private router: Router,
    ) {}

    ngOnInit(): void {
        this.formGroup = this.formBuilder.group({
            name: ['', [Validators.required, Validators.pattern(MODULE_TITLE_PATTERN)]],
            description: [''],
            moduleExampleId: [this.repository.moduleExampleId, Validators.required],
        });

        if (this.moduleExamples.length === 0) {
            this.repositoryService
                .getModuleExamples()
                .pipe(
                    tap((moduleExamples: ModuleRepositoryExample[]) => {
                        this.moduleExamples = moduleExamples;
                        this.placeHolderValue = this.translateService.instant('MODULES.CREATOR.EXAMPLES.PLACEHOLDER', { total: this.moduleExamples.length });
                    }),
                    catchError(() => {
                        this.placeHolderValue = this.translateService.instant('MODULES.CREATOR.EXAMPLES.ERROR_PLACEHOLDER');
                        this.userNotificationService.showError('MODULES.CREATOR.EXAMPLES.ERROR_MESSAGE');
                        return EMPTY;
                    }),
                )
                .subscribe();
        } else {
            this.placeHolderValue = this.translateService.instant('MODULES.CREATOR.EXAMPLES.PLACEHOLDER', { total: this.moduleExamples.length });
        }
    }

    submit(): void {
        if (this.formGroup.valid) {
            this.isSubmitting = true;
            const payload: RepositoryPayload = {
                name: this.formGroup.value.name.trim(),
                moduleExampleId: this.formGroup.value.moduleExampleId === 'null' ? null : this.formGroup.value.moduleExampleId,
                moduleId: this.formGroup.value.moduleId,
            };

            this.userNotificationService.showSpinner();
            this.repositoryService
                .createRepository(payload)
                .pipe(
                    tap((repository: RepositoryPayload) => {
                        this.repository = repository;
                        this.router.navigate([USER_MODULES_ROUTE, repository.moduleId], { queryParams: { isNew: true } });
                        this.userNotificationService.showSuccess('MODULES.CREATOR.CREATE_SUCCESS');
                        this.activeModal.close(payload);
                        this.userNotificationService.hideSpinner();
                    }),
                    catchError(() => {
                        this.userNotificationService.showError('MODULES.CREATOR.CREATE_FAILURE');
                        this.userNotificationService.hideSpinner();
                        return EMPTY;
                    }),
                )
                .subscribe();
        }
    }

    dismiss(reason?: string): void {
        this.activeModal.dismiss(reason);
    }
}
